import { CloseButton, Loader, Popover, Text, TextInput, UnstyledButton } from "@mantine/core";
import { Search } from "lucide-react";
import { useId, useState, type ReactElement, type ReactNode } from "react";

import { useListProducts } from "../../api/generated/endpoints/teams/teams";
import type { Product } from "../../api/generated/model";
import { ErrorAlert } from "../ErrorAlert";
import { selectUpTo } from "../../ui/format";
import classes from "./ProductListPopover.module.css";

/** A product as a form holds it: its id, its name and its first photo. */
export interface ListedProduct {
  productId: string;
  name: string;
  url: string | null;
}

/** A product's first photo, as live shows it: the main one, else a product shot, else any. */
export function listedProduct(product: Product): ListedProduct {
  const photos = product.images.filter((image) => image.url !== null);
  const photo =
    photos.find((image) => image.is_main === true) ??
    photos.find((image) => image.tag === "product_shot") ??
    photos[0];
  return { productId: product.id, name: product.name, url: photo?.url ?? null };
}

export interface ProductListPopoverProps {
  teamId: string;
  brandId: string;
  /** The product picked now, shown active. */
  chosenId: string | null;
  opened: boolean;
  onClose: () => void;
  onPick: (product: ListedProduct) => void;
  /** What it hangs from: `wrap` makes an element its anchor. */
  anchor: (wrap: (element: ReactElement) => ReactElement) => ReactNode;
  position?: "right-start" | "bottom-start" | "top-start";
  /** Live's forms close on a pick; the Ad copy tab's filter stays open (capture advanced-234/09). */
  closeOnPick?: boolean;
}

/**
 * Live's one-product list popover (`ProductSelectPopover`, captures advanced-234/08 and 09):
 * "Products", "You can select up to 1 product" (live's "1 products"), a search by name, the brand's products with their
 * first photo, "No products found". The ad copy forms close it on a pick; the Ad copy tab's filter
 * keeps it open.
 */
export function ProductListPopover(props: ProductListPopoverProps) {
  const { teamId, brandId, chosenId, opened, onClose, onPick, anchor } = props;
  const titleId = useId();
  return (
    <Popover
      opened={opened}
      onChange={(next) => (next ? undefined : onClose())}
      position={props.position ?? "right-start"}
      offset={8}
      trapFocus
      returnFocus
    >
      {anchor((element) => (
        <Popover.Target>{element}</Popover.Target>
      ))}
      <Popover.Dropdown className={classes.dropdown} aria-labelledby={titleId}>
        {/* Mounted while open: each opening starts with an empty search. */}
        <List
          teamId={teamId}
          brandId={brandId}
          chosenId={chosenId}
          titleId={titleId}
          onClose={onClose}
          onPick={(product) => {
            onPick(product);
            if (props.closeOnPick !== false) onClose();
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

interface ListProps {
  teamId: string;
  brandId: string;
  chosenId: string | null;
  titleId: string;
  onClose: () => void;
  onPick: (product: ListedProduct) => void;
}

function List({ teamId, brandId, chosenId, titleId, onClose, onPick }: ListProps) {
  const [query, setQuery] = useState("");
  const products = useListProducts(teamId, brandId);
  const needle = query.trim().toLowerCase();
  const shown = (products.data ?? []).filter((product) =>
    product.name.toLowerCase().includes(needle),
  );
  return (
    <div className={classes.body}>
      <div className={classes.header}>
        <div>
          <Text id={titleId} className={classes.title}>
            Products
          </Text>
          <Text className={classes.subtitle}>{selectUpTo(1, "product")}</Text>
        </div>
        <CloseButton size="sm" aria-label="Close" onClick={onClose} />
      </div>
      <TextInput
        aria-label="Search by product name"
        placeholder="Search by product name"
        leftSection={<Search size={16} aria-hidden />}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        rightSection={
          query === "" ? null : (
            <CloseButton size="sm" aria-label="Clear the search" onClick={() => setQuery("")} />
          )
        }
      />
      {products.isPending ? <Loader size="sm" aria-label="Loading products" m="md" /> : null}
      {products.isError ? <ErrorAlert error={products.error} /> : null}
      {products.isSuccess && shown.length === 0 ? (
        <Text className={classes.empty}>No products found</Text>
      ) : null}
      <div className={classes.list}>
        {shown.map((product) => (
          <Row
            key={product.id}
            product={listedProduct(product)}
            active={product.id === chosenId}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  );
}

function Row(props: {
  product: ListedProduct;
  active: boolean;
  onPick: (product: ListedProduct) => void;
}) {
  const { product, active, onPick } = props;
  const [failed, setFailed] = useState(false);
  return (
    <UnstyledButton className={classes.row} aria-pressed={active} onClick={() => onPick(product)}>
      {product.url === null || failed ? (
        <span className={classes.picture} aria-hidden />
      ) : (
        <img src={product.url} alt="" className={classes.picture} onError={() => setFailed(true)} />
      )}
      <span className={classes.name}>{product.name}</span>
    </UnstyledButton>
  );
}
